import Excel from 'exceljs'
import excel from '../../../utils/excel'
import { THeaderExcel, TContentExcel, TListSpiderChartImage } from './useTableCompareAssessmentReport'
import { useCallback } from 'react'

export type TGenerateExcelProps = {
    title?: string
    header: THeaderExcel[]
    content: Array<Array<TContentExcel>>
    footer: TListSpiderChartImage[]
}

export type TColumn = {
    key: string
    width: number
}

const useExportReportCompareExcel = () => {
    const { createFileDownload, createWorkbook, createWorksheet } = excel

    const generateHeader = useCallback(
        ({
            header,
            worksheet,
            title,
            startRowHeader,
        }: { worksheet: Excel.Worksheet; startRowHeader: number } & Pick<TGenerateExcelProps, 'header' | 'title'>) => {
            const totalCol = header.reduce((totalCol: number, item: THeaderExcel) => totalCol + item.cell, 0)
            worksheet.mergeCells(startRowHeader, 1, startRowHeader, totalCol)
            const row = worksheet.getRow(startRowHeader)
            const cell = row.getCell(totalCol)
            cell.value = title || ''
            cell.alignment = {
                vertical: 'middle',
                horizontal: 'center',
                wrapText: true,
            }

            cell.font = {
                bold: true,
                name: 'TH Sarabun New',
                size: 16,
            }
        },
        [],
    )

    const generateColumns = useCallback(
        ({
            header,
            worksheet,
        }: { worksheet: Excel.Worksheet; startRowHeader: number } & Pick<TGenerateExcelProps, 'header'>) => {
            const columns: TColumn[] = []

            header.forEach((item: THeaderExcel) => {
                let i = item.cell

                while (i > 0) {
                    columns.push({
                        key: `${item.key}-${i}`,
                        width: item.width / item.cell,
                    })
                    i--
                }
            })
            worksheet.columns = columns as Excel.Worksheet['columns']
        },
        [],
    )

    const setCellBorder = useCallback((row: Excel.Row, colKey: number) => {
        row.getCell(colKey).border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' },
        }
    }, [])
    const generateTableHeader = useCallback(
        ({
            header,
            minHeight,
            worksheet,
            startRowHeaderTable,
            startRowHeader,
        }: {
            worksheet: Excel.Worksheet
            startRowHeaderTable: number
            minHeight: number
            startRowHeader: number
        } & Pick<TGenerateExcelProps, 'header'>) => {
            generateColumns({
                header,
                startRowHeader,
                worksheet,
            })

            const startRowTableHeader = startRowHeaderTable
            const row = worksheet.getRow(startRowTableHeader)
            row.height = minHeight
            let currentColumn = 1

            header.forEach(async (item: THeaderExcel) => {
                if (item.cell > 1) {
                    // merge by start row, start column, end row, end column (equivalent to K10:M12)
                    worksheet.mergeCells(
                        startRowTableHeader,
                        currentColumn,
                        startRowTableHeader,
                        currentColumn + (item.cell - 1),
                    )
                }
                const cell = row.getCell(currentColumn)
                cell.value = item.name
                cell.alignment = {
                    vertical: 'middle',
                    horizontal: 'center',
                    wrapText: true,
                }
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: {
                        argb: '093018',
                    },
                }
                cell.font = {
                    color: { argb: '00FFFFFF' },
                    name: 'TH Sarabun New',
                    size: 16,
                }

                setCellBorder(row, currentColumn)
                currentColumn += item.cell
            })
        },
        [generateColumns, setCellBorder],
    )
    const generateTableContent = useCallback(
        ({
            content,
            minHeight,
            worksheet,
            startRowHeaderTable,
        }: {
            worksheet: Excel.Worksheet
            startRowHeaderTable: number
            minHeight: number
        } & Pick<TGenerateExcelProps, 'content'>) => {
            content.forEach((dataRow: Array<TContentExcel>, index1: number) => {
                const row = worksheet.getRow(startRowHeaderTable + (index1 + 1))
                row.height = 50
                dataRow.forEach((dataColumn: TContentExcel, index2: number) => {
                    const cell = row.getCell(index2 + 1)
                    cell.value = dataColumn || dataColumn === 0 ? dataColumn : index1 === 0 ? '' : '-'

                    const horizontal = index1 === 0 ? 'center' : index2 === 0 ? 'left' : 'center'
                    cell.alignment = {
                        vertical: 'middle',
                        horizontal: horizontal,
                        wrapText: true,
                    }

                    if (content.length === index1 + 1 && index2 > 0) {
                        cell.numFmt = '0.00%'
                    } else {
                        cell.numFmt = '0.00'
                    }

                    if (index2 !== 0) {
                        const bgColor = index2 % 2 === 0 ? 'FEFCF2' : 'F0F9F8'
                        cell.fill = {
                            type: 'pattern',
                            pattern: 'solid',
                            fgColor: {
                                argb: bgColor,
                            },
                        }
                    }
                    if (index2 === 0) {
                        cell.font = {
                            bold: true,
                            name: 'TH Sarabun New',
                            size: 16,
                        }
                    } else {
                        cell.font = {
                            name: 'TH Sarabun New',
                            size: 16,
                        }
                    }

                    setCellBorder(row, index2 + 1)
                })
            })
        },
        [setCellBorder],
    )
    const generateFooter = useCallback(
        ({
            worksheet,
            workbook,
            footer,
            startRowFooter,
        }: { workbook: Excel.Workbook; worksheet: Excel.Worksheet; startRowFooter: number } & Pick<
            TGenerateExcelProps,
            'footer'
        >) => {
            let currentRowFooter = startRowFooter

            footer.forEach((item: TListSpiderChartImage) => {
                const row = worksheet.getRow(currentRowFooter)
                row.getCell(1).value = item.title
                row.font = {
                    name: 'TH Sarabun New',
                    size: 16,
                    bold: true,
                }

                const imageId = workbook.addImage({
                    base64: item.image,
                    extension: 'png',
                })

                worksheet.addImage(imageId, {
                    tl: { col: 0, row: currentRowFooter + 1 },
                    ext: { width: 305, height: 305 },
                })

                currentRowFooter += 20
            })
        },
        [],
    )

    const generateExcel = useCallback(
        ({ title = '', header, content, footer }: TGenerateExcelProps) => {
            const startRowHeader = 1 //ใช้กำหนดแถวที่เป็นหัวเรื่อง
            const startRowHeaderTable = 3 //ใช้กำหนดแถวที่จะเป็นหัวตาราง
            const startRowFooter = startRowHeaderTable + content.length + 2 // + 2 คือ 1 สำหรับตัวตาราง 1 สำหรับเว้น 1 บรรทัด
            const minHeight = 80

            const workbook = createWorkbook() //สร้าง instance

            const worksheet = createWorksheet({
                workbook,
                sheetName: 'เปรียบเทียบคะแนนการประเมิน',
                options: {
                    //จำเป็นต้องตั้งค่าส่วนนี้
                    pageSetup: {
                        paperSize: 9, //follow documentation the 9 value is A4
                        orientation: 'landscape', //แนวตั้ง
                    },
                },
            })

            workbook.creator = 'QUESTIONNAIRE-WEB' //ผู้สร้าง
            workbook.lastModifiedBy = 'QUESTIONNAIRE-WEB' //ผู้แก้ไขล่าสุด

            const generateTable = () => {
                generateTableHeader({
                    header,
                    minHeight,
                    startRowHeader,
                    startRowHeaderTable,
                    worksheet,
                })
                generateTableContent({
                    content,
                    minHeight,
                    startRowHeaderTable,
                    worksheet,
                })
            }

            const generateContentProcess = () => {
                generateHeader({
                    header,
                    startRowHeader,
                    worksheet,
                    title,
                })
                generateTable()
                generateFooter({
                    footer,
                    startRowFooter,
                    workbook,
                    worksheet,
                })
            }

            const mainProcess = () => {
                generateContentProcess()
                createFileDownload({ workbook, fileName: 'รายงานเปรียบเทียบคะแนนการประเมิน.xlsx' })
            }

            mainProcess()
        },
        [
            createFileDownload,
            createWorkbook,
            createWorksheet,
            generateFooter,
            generateHeader,
            generateTableContent,
            generateTableHeader,
        ],
    )

    const downloadExcel = (props: TGenerateExcelProps) => {
        generateExcel(props)
    }

    return {
        downloadExcel,
        generateHeader,
        generateColumns,
        setCellBorder,
        generateTableHeader,
        generateTableContent,
        generateFooter,
    }
}

export default useExportReportCompareExcel
